import { useEffect, useRef, useState } from "react";
import { Moon, LockKeyhole, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fridayPrayerTime } from "@/lib/prayer";

const mins = (hhmm) => { const [h, m] = String(hhmm || "13:00").split(":").map(Number); return h * 60 + (m || 0); };

export function fridayState(shop, now = new Date()) {
  if (!shop?.friday_lock_enabled || now.getDay() !== 5) return { phase: "off" };
  const cur = now.getHours() * 60 + now.getMinutes();
  const prayer = fridayPrayerTime(shop, now);
  const start = mins(prayer) - 30;
  const end = mins(prayer) + (Number(shop.friday_lock_minutes) || 60);
  if (cur >= start && cur < end) return { phase: "locked", end, left: end - cur };
  if (cur >= start - 240 && cur < start) return { phase: "warn", start, left: start - cur };
  return { phase: "off" };
}

export default function FridayLock() {
  const { view, t, staff } = useApp();
  const [tick, setTick] = useState(0);
  const [override, setOverride] = useState(false);
  const lastWarn = useRef(null);
  useEffect(() => { const id = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(id); }, []);
  const st = fridayState(view.shop);
  useEffect(() => {
    if (st.phase !== "warn") { lastWarn.current = null; return; }
    const slot = Math.floor(st.left / 30);
    if (lastWarn.current === slot) return;
    lastWarn.current = slot;
    const h = Math.floor(st.left / 60), m = st.left % 60;
    toast.info(t("friday_warn", { time: h ? `${h}h ${m}m` : `${m} min` }), { duration: 15000, icon: <Moon className="w-5 h-5 text-[#1F4E8C]" /> });
  }, [st.phase, st.left, tick, t]);
  useEffect(() => { if (st.phase !== "locked") setOverride(false); }, [st.phase]);
  if (st.phase !== "locked" || override) return null;
  const endH = String(Math.floor(st.end / 60)).padStart(2, "0"), endM = String(st.end % 60).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-[200] cd-root text-white grid place-items-center text-center p-6" data-testid="friday-lock">
      <div className="cd-stars" aria-hidden="true" />
      <div className="relative cd-glass px-12 py-10 max-w-xl">
        <Moon className="w-20 h-20 mx-auto text-[#FFD24D]" />
        <div className="text-4xl font-heading mt-4">{t("friday_title")}</div>
        <div className="text-lg text-white/70 mt-3">{t("friday_body")}</div>
        <div className="text-2xl font-bold mt-5 tabular-nums" dir="ltr" data-testid="friday-lock-until">{t("friday_until")} {endH}:{endM} · {st.left} min</div>
        <div className="mt-6 flex items-center justify-center gap-2 text-white/50 text-sm"><LockKeyhole className="w-4 h-4" />{t("friday_locked_by")}</div>
        {staff?.role === "owner" && <button type="button" onClick={() => setOverride(true)} className="mt-6 h-11 px-5 rounded-xl border border-white/30 bg-white/10 font-bold flex items-center gap-2 mx-auto hover:bg-white/20" data-testid="friday-override"><Unlock className="w-5 h-5" />{t("friday_override")}</button>}
      </div>
    </div>
  );
}
